import { useTranslation } from '../presentation/common/useTranslation.js';
import { useState, useEffect } from 'react';

import { Shield, Cookie, Check, X, Sliders, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  COOKIE_CONSENT_KEY, 
  UMAMI_DISABLED_KEY,
  DIAGNOSTICS_CONSENT_KEY,
  isDiagnosticsEnabled,
  setTrackingConsent, 
  openCookieBanner, 
  isTrackingEnabled 
} from '../utils/cookieConsent';

export { COOKIE_CONSENT_KEY, UMAMI_DISABLED_KEY, openCookieBanner, isTrackingEnabled };

export default function CookieBanner() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(false);
  const [diagnosticsEnabled, setDiagnosticsEnabled] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    const umamiDisabled = localStorage.getItem(UMAMI_DISABLED_KEY);

    setDiagnosticsEnabled(isDiagnosticsEnabled());
    if (consent === null || localStorage.getItem(DIAGNOSTICS_CONSENT_KEY) === null) {
      setIsOpen(true);
      setAnalyticsEnabled(isTrackingEnabled());
    } else {
      setAnalyticsEnabled(consent === 'granted' && umamiDisabled !== '1');
    }

    const handleOpen = () => {
      const currentConsent = localStorage.getItem(COOKIE_CONSENT_KEY);
      const currentUmamiDisabled = localStorage.getItem(UMAMI_DISABLED_KEY);
      setAnalyticsEnabled(currentConsent === 'granted' && currentUmamiDisabled !== '1');
      setDiagnosticsEnabled(isDiagnosticsEnabled());
      setShowDetails(false);
      setIsOpen(true);
    };

    window.addEventListener('open-cookie-banner', handleOpen);
    return () => window.removeEventListener('open-cookie-banner', handleOpen);
  }, []);

  const handleAccept = () => {
    setTrackingConsent(true, true);
    setDiagnosticsEnabled(true);
    setAnalyticsEnabled(true);
    setIsOpen(false);
  };

  const handleDecline = () => {
    setTrackingConsent(false);
    setDiagnosticsEnabled(false);
    setAnalyticsEnabled(false);
    setIsOpen(false);
  };

  const handleSaveCustom = () => {
    setTrackingConsent(analyticsEnabled, diagnosticsEnabled);
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <aside 
      aria-label={t('cookie.title', 'Datenschutz & Tracking-Einstellungen')}
      className="cookie-consent-banner fixed bottom-0 left-0 right-0 z-[100] max-h-[85dvh] overflow-y-auto p-4 sm:p-6 bg-[#091520]/95 backdrop-blur-md border-t border-[#bda16b]/30 shadow-[0_-10px_30px_rgba(0,0,0,0.5)] text-zinc-100 transition-all duration-300"
    >
      <div className="max-w-5xl mx-auto flex flex-col gap-4">
        {/* Banner Header & Main Description */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2.5 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 shrink-0 mt-0.5">
              <Cookie className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-serif text-base sm:text-lg font-semibold text-[#eed8ab] flex items-center gap-2">
                {t('cookie.title', 'Datenschutz & Tracking-Einstellungen')}
              </h3>
              <p className="text-xs sm:text-sm text-zinc-300 mt-1 leading-relaxed max-w-3xl">
                {t('cookie.description', 'Wir nutzen Umami Analytics, um die Plattform datenschutzfreundlich und ohne invasive Cookies zu verbessern. Du kannst die anonyme Datenerfassung jederzeit aktivieren oder deaktivieren.')}
              </p>
            </div>
          </div>

          <button
            onClick={handleDecline}
            className="text-zinc-400 hover:text-zinc-200 p-1.5 rounded-md hover:bg-white/5 transition-colors sm:hidden"
            title={t('dialogs.close', 'Schließen')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Detailed Options Drawer */}
        {showDetails && (
          <div className="mt-2 pt-4 border-t border-zinc-800/80 space-y-3 animate-in fade-in duration-200">
            {/* Essential cookies */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/60 border border-zinc-800">
              <div className="flex items-center gap-3">
                <Lock className="w-4 h-4 text-emerald-400 shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-zinc-200">
                    {t('cookie.essential_title', 'Essenzielle Funktionen')}
                  </div>
                  <div className="text-[11px] text-zinc-400">
                    {t('cookie.essential_desc', 'Session, Sprach- und Theme-Einstellungen. Immer aktiv.')}
                  </div>
                </div>
              </div>
              <span className="text-[11px] font-mono font-medium px-2 py-0.5 rounded bg-emerald-950/60 text-emerald-400 border border-emerald-800/40">
                    {t('cookie.always_active', 'Immer aktiv')}
              </span>
            </div>

            {/* Analytics toggle */}
            <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/60 border border-zinc-800">
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-amber-400 shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-zinc-200">
                    {t('cookie.analytics_title', 'Umami Analytics (Web-Tracking)')}
                  </div>
                  <div className="text-[11px] text-zinc-400">
                    {t('cookie.analytics_desc', 'Anonyme statistische Auswertung zur Verbesserung von Goodvale.')}
                  </div>
                </div>
              </div>
              
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={analyticsEnabled}
                  aria-label={t('cookie.analytics_title')}
                  onChange={(e) => setAnalyticsEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-zinc-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-zinc-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-amber-600"></div>
              </label>
            </div>
          </div>
        )}

        {showDetails && <label className="flex items-center justify-between gap-4 p-3 rounded-lg bg-zinc-900/60 border border-zinc-800">
          <span><span className="block text-xs font-semibold">{t('cookie.diagnostics_title')}</span><span className="block text-[11px] text-zinc-400">{t('cookie.diagnostics_desc')}</span></span>
          <input type="checkbox" checked={diagnosticsEnabled} onChange={event => setDiagnosticsEnabled(event.target.checked)} className="accent-amber-500" />
        </label>}
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
          <div className="flex items-center gap-4 text-xs">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="text-amber-400/90 hover:text-amber-300 underline underline-offset-4 font-medium flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <Sliders className="w-3.5 h-3.5" />
              {showDetails ? t('dialogs.close', 'Schließen') : t('cookie.settings', 'Einstellungen')}
            </button>
            <Link
              to="/legal?tab=privacy"
              className="text-zinc-400 hover:text-zinc-200 underline underline-offset-4 transition-colors"
            >
              {t('cookie.privacy_policy', 'Datenschutzerklärung')}
            </Link>
          </div>

          <div className="flex items-center gap-2.5">
            {showDetails ? (
              <button
                onClick={handleSaveCustom}
                className="w-full sm:w-auto px-4 py-2 text-xs font-serif font-semibold bg-gradient-to-r from-amber-600 to-amber-700 hover:brightness-110 text-white rounded-md shadow transition-all flex items-center justify-center gap-1.5 cursor-pointer"
              >
                <Check className="w-4 h-4" />
                {t('cookie.save', 'Einstellungen speichern')}
              </button>
            ) : (
              <>
                <button
                  onClick={handleDecline}
                  className="flex-1 sm:flex-initial px-4 py-2 text-xs font-serif font-medium text-zinc-300 hover:text-white bg-zinc-800/80 hover:bg-zinc-800 border border-zinc-700 rounded-md transition-colors text-center cursor-pointer"
                >
                  {t('cookie.decline_all', 'Tracking deaktivieren')}
                </button>
                <button
                  onClick={handleAccept}
                  className="flex-1 sm:flex-initial px-4 py-2 text-xs font-serif font-medium text-zinc-300 hover:text-white bg-zinc-800/80 hover:bg-zinc-800 border border-zinc-700 rounded-md transition-colors text-center cursor-pointer"
                >
                  {t('cookie.accept_all', 'Tracking erlauben')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
